import {getRequest} from '@/Services/ApiService';
import {reportError} from '@/Services/Logger';
import APIURL from '@/api-url';
import styles from '@/styles/blogDetail.module.scss';
import {MUIBox, MUIContainer, MUIGrid, MUISpinner, MUITypography} from '@/components/MUI-Components';
import {NextPageContext} from 'next';
import dynamic from 'next/dynamic';
import PageHead from '@/components/PageHead';
import JsonLd from '@/components/Common/JsonLd';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import parse from 'html-react-parser';
const CommentForm = dynamic(() => import('@/components/Ui-components/CommentForm'));
const ContactForm = dynamic(() => import('@/components/Ui-components/Form'));
import {fixSchemaDomain, cleanFaqNodes, buildBlogPostingSchema} from '@/util/seo-utils';

import {useScrollHighlight, scrollToHeading} from '@/components/blog-shared/TableOfContents';
import RelatedPosts, {InlineRelatedPost} from '@/components/blog-shared/RelatedPosts';
import {
    splitHtmlByRelatedPostsShortcode,
    stripHtml,
    decodeHtmlEntities,
    addTargetBlank,
    unwrapCodeWrappedCtaBlocks,
    addFaqQuestionClass,
    faqQuestionToH3,
    addTableClasses,
    addImageClasses,
    addHeadingClasses
} from '@/util/html-parser-utils';
import {
    makeRelatedCardParseOptions,
    makeReadMoreParseOptions,
    makeCtaBlocksParseOptions,
    replaceContentImage
} from '@/components/blog-shared/html-parser';
import {SITE_URL} from '@/constants/urls';
import {setEdgeCache} from '@/util/edgeCache';
import BlogBreadcrumb from '@/components/blog-shared/blog-detail/BlogBreadcrumb';
import BlogHero from '@/components/blog-shared/blog-detail/BlogHero';
import BlogAuthorBar from '@/components/blog-shared/blog-detail/BlogAuthorBar';
import BlogAuthorCards from '@/components/blog-shared/blog-detail/BlogAuthorCards';
import BlogTocSidebar from '@/components/blog-shared/blog-detail/BlogTocSidebar';
import BlogKeyTakeaways from '@/components/blog-shared/blog-detail/BlogKeyTakeaways';
import BlogReviewSlider from '@/components/blog-shared/blog-detail/BlogReviewSlider';
import BlogComments from '@/components/blog-shared/blog-detail/BlogComments';
import {blogExpertFormWrapperSx} from '@/styles/blogExpertFormWrapperSx';
import {commentFormWrapperSx} from '@/styles/commentFormWrapperSx';
import {estimateReadTime, mapRelatedUseCases, slugFromLink} from '@/util/useCaseAdapter';
import {fetchUseCaseCategories, withCategoryDisplayName} from '@/util/useCaseListing';
import {resolveUseCaseAuthor, USE_CASE_REVIEWER, type UseCasePerson} from '@/constants/useCaseAuthors';

// ─── Module-level style constants ─────────────────────────────────────────────
// stretch lets the sidebar column fill the row height, giving the sticky
// form/slider group room to travel as the article scrolls.
const stickyGridSx = {alignSelf: 'stretch'} as const;
// The sticky group already pins the form. With no AI-tools card above it, the form's own
// `sticky; top: 100px` (shared with the blog) slides it down over the review slider; the top
// margin gives back the breathing room the card used to leave above the form.
const formWrapperSx = {...blogExpertFormWrapperSx, position: 'static !important', mt: 3} as const;

// This route's own section identity, threaded through the shared blog-detail
// components (breadcrumb, related cards, schema) which default to the blog.
const BASE_PATH = '/use-cases';
const SECTION_LABEL = 'Use Cases';
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Top-level TOC entries from the backend payload, for the SSR seed. Uses
 * `decodeHtmlEntities` (regex-based, works on the server) rather than the
 * DOM textarea trick the client effect uses.
 */
const tocFromPayload = (toc: {level: number; text: string; slug: string}[]): string[] =>
    toc.filter((i) => i.level === 2).map((i) => decodeHtmlEntities(i.text ?? '').trim());

const UseCaseDetail = (props: any) => {
    const {blogData, category} = props;
    // Set server-side from the use case's categories (see useCaseAuthors); null outside the mapped topics.
    const authorInfo: UseCasePerson | null = props.authorInfo ?? null;
    const reviewerInfo = authorInfo ? USE_CASE_REVIEWER : undefined;

    // `og_image` is the last fallback because production WordPress does not yet
    // expose `blog_image` on this post type — without it the hero would drop to
    // the skeleton placeholder.
    const heroImage = blogData?.blog_image || blogData?.featured_image_url || blogData?.yoast_head_json?.og_image?.[0]?.url || '';
    const categories = blogData?.selected_categories ?? [];
    const keyTakeaways: string[] = blogData?.key_takeaways ?? [];
    const tableOfContents: {level: number; text: string; slug: string}[] = blogData?.table_of_contents ?? [];

    const rawContent = blogData?.content?.rendered ?? '';
    // No backend-computed `read_time` on this post type — derive it from the content.
    const readTime = useMemo(() => blogData?.read_time || estimateReadTime(rawContent), [blogData?.read_time, rawContent]);

    // Structured CTA. `use_case` sends `cta` as an empty string and has no
    // `cta_blocks`, so this stays empty and the CTA replacer only picks up
    // WordPress-native `.cta-stack` groups authored inside the content itself.
    const ctaBlocks = useMemo(() => {
        const rawCtaBlocks: any[] = Array.isArray(blogData?.cta_blocks) ? blogData.cta_blocks : [];
        const ctaData = (typeof blogData?.cta === 'object' && blogData?.cta) || {};
        return rawCtaBlocks.length > 0
            ? rawCtaBlocks.map((b: any) => ({
                  tag: b?.badge || '',
                  title: b?.title || '',
                  description: b?.description || '',
                  btnLabel: b?.button_text || '',
                  btnHref: b?.button_url || ''
              }))
            : [
                  {
                      tag: ctaData.tag || '',
                      title: ctaData.title || '',
                      description: ctaData.description || '',
                      btnLabel: ctaData.btn_label || '',
                      btnHref: ctaData.btn_href || ''
                  }
              ];
    }, [blogData]);

    // `selected_categories` is missing on production WordPress for this post type,
    // so the taxonomy term fetched server-side is the fallback for the hero pill.
    const blogCategoryName = categories.filter((item: any) => item !== 'All Topics')?.[0] ?? category?.name ?? '';

    const title = typeof blogData?.title === 'string' ? blogData.title : blogData?.title?.rendered ?? '';
    // `use_case` registers neither `excerpt` nor `meta.description` in REST, so the
    // Yoast description is the only summary source (the excerpt paths stay as a
    // fallback in case the backend adds them later).
    const excerpt = decodeHtmlEntities(
        blogData?.yoast_head_json?.description ||
            blogData?.meta?.description ||
            (blogData?.excerpt?.rendered
                ? stripHtml(blogData.excerpt.rendered)
                      .replace(/\[…\]|\[\.\.\.\]/g, '')
                      .trim()
                : '')
    );

    // ── Structured data ───────────────────────────────────────────────────────
    // Article (not BlogPosting) — a use case isn't a blog post. The `author` node is the
    // byline shown on the page (none outside the mapped topics). No BreadcrumbList: the Yoast
    // graph below already ships one, so a second would be a duplicate.
    const articleSchema = buildBlogPostingSchema(blogData, {
        title,
        heroImage,
        basePath: BASE_PATH,
        schemaType: 'Article',
        authorInfo: authorInfo ?? undefined,
        omitAuthor: !authorInfo
    });

    const yoastSchema = blogData?.yoast_head_json?.schema
        ? JSON.stringify(
              fixSchemaDomain({
                  ...blogData.yoast_head_json.schema,
                  '@graph': cleanFaqNodes(blogData.yoast_head_json.schema['@graph'])
              })
          )
        : undefined;
    // ─────────────────────────────────────────────────────────────────────────

    const currentBlogId = blogData?.id;
    const {categoryResponse, latestInsights, inlineRelatedPosts = [], readLinkPosts = []} = props;
    const hasDebutCard = rawContent.includes('debut-related-card');
    const hasRawShortcode = /\[related_post(?:_\d+)?\]/.test(rawContent);
    const hasInlineCtaBlock = /\[cta_block(?:_\d+)?\]/.test(rawContent) || rawContent.includes('debut-cta-block');
    const [commentData, setCommentData] = useState<any>();
    // Seeded from the server payload (not an effect) so the TOC's internal links
    // exist in the raw HTML instead of appearing a tick after hydration. The effect
    // below only has to cover the legacy no-`table_of_contents` case, which needs
    // the DOM to extract <h2>s.
    const [tocItems, setTocItems] = useState<string[]>(() => tocFromPayload(tableOfContents));
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const tocListRef = useRef<HTMLElement | null>(null);
    const [loading, setLoading] = useState(false);

    const setActiveIndexStable = useCallback((i: number) => setActiveIndex(i), []);
    useScrollHighlight(setActiveIndexStable);

    useEffect(() => {
        if (activeIndex === null || !tocListRef.current) return;
        const activeItem = tocListRef.current.children[activeIndex] as HTMLElement;
        if (activeItem) {
            activeItem.scrollIntoView({block: 'nearest', behavior: 'smooth'});
        }
    }, [activeIndex]);

    useEffect(() => {
        if (!blogData) return;
        setLoading(true);

        // Decodes HTML entities (e.g. backend sends "Introduction&nbsp;") and trims,
        // so the TOC never shows raw entity text.
        const decodeEntities = (s: string) => {
            const el = document.createElement('textarea');
            el.innerHTML = s;
            return (el.value || '').replace(/\u00a0/g, ' ').trim();
        };

        const toc: {level: number; text: string; slug: string}[] = blogData.table_of_contents ?? [];
        if (toc.length > 0) {
            // Re-run through the DOM-based decoder: it resolves every named entity,
            // where the SSR seed only handles the common ones.
            setTocItems(toc.filter((i) => i.level === 2).map((i) => decodeEntities(i.text)));
        } else {
            const container = document.createElement('div');
            container.innerHTML = blogData?.content?.rendered ?? '';
            const extracted = Array.from(container.getElementsByTagName('h2')).map((el) => el.textContent ?? '');
            setTocItems(extracted);
        }

        if (!blogData.id) {
            setCommentData([]);
            setLoading(false);
            return;
        }

        getRequest(`${APIURL.COMMENT_LISTING}?post=${blogData.id}`)
            .then((res: any) => setCommentData(res))
            .catch(() => setCommentData([]))
            .finally(() => setLoading(false));
    }, [blogData]);

    // Increment view count once per page visit (fire-and-forget). The `debut/v1`
    // endpoint is keyed by post id, so it serves use cases as well as blog posts.
    useEffect(() => {
        if (!blogData?.id) return;
        getRequest(`${APIURL.BLOG_VIEW}/${blogData.id}/view`).catch((_err) => undefined);
    }, [blogData?.id]);

    // Desktop only: tie the sticky sidebar's internal scroll to the article's
    // scroll progress. The sidebar (AI card + form + review slider) is taller than
    // the viewport, so as the user reads the article it auto-scrolls in sync —
    // revealing the form/slider by the end instead of getting cut off.
    useEffect(() => {
        const sync = () => {
            if (globalThis.innerWidth < 1200) return;
            const sidebar = document.querySelector('.bdn-content-sidebar') as HTMLElement | null;
            const section = document.getElementById('bdn-description-section');
            if (!sidebar || !section) return;
            const extra = sidebar.scrollHeight - sidebar.clientHeight;
            if (extra <= 0) return;
            const range = section.offsetHeight - globalThis.innerHeight;
            const frac = range > 0 ? (globalThis.scrollY - section.offsetTop) / range : 0;
            sidebar.scrollTop = Math.min(extra, Math.max(0, frac * extra));
        };
        globalThis.addEventListener('scroll', sync, {passive: true});
        globalThis.addEventListener('resize', sync);
        sync();
        return () => {
            globalThis.removeEventListener('scroll', sync);
            globalThis.removeEventListener('resize', sync);
        };
    }, [blogData]);

    const tocSlugs = tableOfContents.filter((i) => i.level === 2).map((i) => i.slug);

    const handleTocClick = useCallback(
        (index: number) => {
            setActiveIndex(index);
            const slug = tocSlugs[index];
            if (slug) {
                const el = document.getElementById(slug);
                if (el) {
                    el.scrollIntoView({behavior: 'smooth', block: 'start'});
                    return;
                }
            }
            scrollToHeading('bdn-description-section', index);
        },
        [tocSlugs]
    );

    // The transform chain + parse() is expensive over a full article. Its inputs all
    // derive from the (stable) server payload, so memoize it — otherwise it re-runs
    // on every scroll/slider/bookmark state change.
    const renderedContent = useMemo(() => {
        if (!rawContent) return null;
        const processedHtml = addImageClasses(
            addTableClasses(addHeadingClasses(addFaqQuestionClass(faqQuestionToH3(addTargetBlank(unwrapCodeWrappedCtaBlocks(rawContent))))))
        );

        // Single CTA replacer instance — its internal counter maps the Nth CTA block
        // to ctaBlocks[N]. Reused across every parse() pass below so numbering stays
        // correct across chunks.
        const ctaOpts = makeCtaBlocksParseOptions(ctaBlocks);
        // "Read more / Read also – <a>..." paragraphs → bdn-related-section card design.
        const allPosts = [...(categoryResponse ?? []), ...(latestInsights ?? []), ...(readLinkPosts ?? [])];
        const readMoreOpts = makeReadMoreParseOptions(blogCategoryName, allPosts);

        // Merged replacer: runs readMore → cta in order. Used in all parse passes.
        const mergedReplace = (node: any, index: any) => {
            // Upgrade content <img> → next/image (responsive srcset) so body images stay sharp.
            const img = replaceContentImage(node);
            if (img !== undefined) return img;
            const rm = readMoreOpts.replace?.(node, index);
            if (rm !== undefined && rm !== false) return rm;
            return ctaOpts.replace?.(node, index);
        };

        // Case 1: WordPress processed the shortcode to debut-related-card HTML.
        if (hasDebutCard) {
            const relatedOpts = makeRelatedCardParseOptions(blogCategoryName);
            return parse(processedHtml, {
                replace: (node, index) => {
                    const r = relatedOpts.replace?.(node, index);
                    if (r !== undefined && r !== false) return r;
                    return mergedReplace(node, index);
                }
            });
        }
        // Case 2: WordPress rendered [cta_block_N] as <div class="debut-cta-block">
        if (hasInlineCtaBlock) {
            return parse(processedHtml, {replace: mergedReplace});
        }
        // Case 3: Raw [related_post]/[related_post_N] shortcode text — split and inject
        // the use cases the WordPress team explicitly linked. No category fallback.
        if (hasRawShortcode) {
            const chunks = splitHtmlByRelatedPostsShortcode(processedHtml);
            const explicitPosts = inlineRelatedPosts.filter((p: any) => p.id !== currentBlogId);
            if (chunks.length === 1) return parse(chunks[0], {replace: mergedReplace});
            return chunks.map((chunk, i) => (
                <React.Fragment key={i}>
                    {parse(chunk, {replace: mergedReplace})}
                    {i < chunks.length - 1 && explicitPosts[i] && (
                        <InlineRelatedPost item={explicitPosts[i]} basePath={BASE_PATH} categoryLabel={blogCategoryName} />
                    )}
                </React.Fragment>
            ));
        }
        // Case 4: No related shortcode — still replace any CTA blocks + read-more links
        return parse(processedHtml, {replace: mergedReplace});
    }, [
        rawContent,
        ctaBlocks,
        hasDebutCard,
        hasInlineCtaBlock,
        hasRawShortcode,
        blogCategoryName,
        categoryResponse,
        currentBlogId,
        inlineRelatedPosts,
        latestInsights,
        readLinkPosts
    ]);

    return (
        <>
            <PageHead
                title={blogData?.yoast_head_json?.title}
                description={blogData?.yoast_head_json?.description}
                image={heroImage}
                canonical={blogData?.yoast_head_json?.og_url ?? `${SITE_URL}${BASE_PATH}/${blogData?.slug}`}
                ogTitle={title}
                ogDescription={blogData?.yoast_head_json?.description}
                ogLocale="en_US"
                schema={yoastSchema}
            />
            {articleSchema && <JsonLd data={articleSchema} />}

            <MUIBox className={`${styles.bdnPage} bdn-page`}>
                {blogData?.title && (
                    <MUIContainer>
                        {/* ── Breadcrumb ── */}
                        <BlogBreadcrumb blogCategoryName={blogCategoryName} basePath={BASE_PATH} sectionLabel={SECTION_LABEL} />

                        {/* ── Hero: title left, image right ── */}
                        <BlogHero
                            blogCategoryName={blogCategoryName}
                            title={title}
                            excerpt={excerpt}
                            blogData={blogData}
                            readTime={readTime}
                            heroImage={heroImage}
                        />

                        {/* ── Author bar ── */}
                        {authorInfo && <BlogAuthorBar authorInfo={authorInfo} reviewerInfo={reviewerInfo} />}

                        {/* ── TOC + Content + Sidebar ── */}
                        <MUIBox className="tabel-blog-outer">
                            <MUIGrid container className="table-content-wrapper" spacing={{xs: 0, sm: 3}} gap={{xs: 2, sm: 0}}>
                                <MUIGrid item xs={12} sm={3} md={2.5} className="table-blog-content" sx={{alignSelf: 'flex-start'}}>
                                    <BlogTocSidebar
                                        tocItems={tocItems}
                                        activeIndex={activeIndex}
                                        handleTocClick={handleTocClick}
                                        tocListRef={tocListRef}
                                    />
                                </MUIGrid>
                                <MUIGrid item xs={12} sm={9} md={7} className="bdn-blog-content" id="bdn-description-section">
                                    {/* ── Key Takeaways card ── */}
                                    <BlogKeyTakeaways keyTakeaways={keyTakeaways} />
                                    {renderedContent}

                                    {/* ── Author & Reviewer cards ── */}
                                    {authorInfo && <BlogAuthorCards authorInfo={authorInfo} reviewerInfo={reviewerInfo} />}
                                </MUIGrid>
                                <MUIGrid item xs={12} md={2.5} sx={stickyGridSx} className="bdn-content-sidebar" data-lenis-prevent>
                                    {/* ── Sticky group: form + sliders ── */}
                                    <MUIBox className="bdn-sticky-group" data-lenis-prevent>
                                        <MUIBox className="use-case-hupspot-form-wrapper" sx={formWrapperSx}>
                                            <ContactForm
                                                type="normal"
                                                formHeading="Talk To Our Expert"
                                                submitBtn="Book a free call →"
                                                classNameForm="blog-side-form"
                                                submitBtnClass="button-bg-primary animated-button"
                                                smallForm={true}
                                            />
                                        </MUIBox>

                                        {/* ── Slider: Review platforms ── */}
                                        <BlogReviewSlider />
                                    </MUIBox>
                                </MUIGrid>
                            </MUIGrid>
                        </MUIBox>

                        {/* ── Related use cases ── */}
                        <RelatedPosts
                            items={latestInsights ?? categoryResponse ?? []}
                            currentId={currentBlogId}
                            basePath={BASE_PATH}
                            heading="Our Related Insights"
                        />

                        {/* ── Comments ── */}
                        <BlogComments commentData={commentData} />

                        {/* ── Comment form ── */}
                        <MUIBox className="margin" sx={commentFormWrapperSx}>
                            <MUITypography className="spectrum-heading color-black font-headers comment-form-heading">
                                Leave a Comment
                            </MUITypography>
                            <MUITypography className="comment-form-subheading">Your voice matters to us</MUITypography>
                            {blogData?.id && <CommentForm postId={blogData.id} />}
                        </MUIBox>
                    </MUIContainer>
                )}
            </MUIBox>

            {loading ? <MUISpinner /> : null}
        </>
    );
};

export {UseCaseDetail};
export default UseCaseDetail;
// Same as the listing and category pages: header + footer without the blue category bar.
UseCaseDetail.Layout = 'Blog';

/** Fields the related/read-more cards need from the use-case listing endpoint. */
const LISTING_FIELDS = 'id,title,slug,blog_image,date,featured_media,selected_categories,yoast_head_json';

export const getServerSideProps: any = async (context: NextPageContext) => {
    const {query, res}: any = context;

    try {
        // NOTE: no `_fields` filter — WordPress strips custom REST fields
        // (table_of_contents, key_takeaways, view_count, related_use_cases, …)
        // when `_fields` is used. Single-post server fetch, so the full payload is fine.
        const response = await getRequest<any>(`${APIURL.USE_CASE}?slug=${query?.useCase}`);
        const blogData = response?.[0] || null;
        // Slug genuinely does not exist in the CMS → real HTTP 404 (not a 200 skeleton).
        // This is a `return`, not a `throw`, so it never falls through to the 500 handler below.
        if (!blogData) return {notFound: true};

        // A real use case with no category must still render — related content is
        // optional and never a 404/500 trigger.
        const id = blogData?.use_cases_categories && blogData.use_cases_categories.length > 0 ? blogData.use_cases_categories.at(-1) : null;

        // ── Explicit related use cases set by the WordPress team ──────────────
        // `related_use_cases` carries absolute permalinks instead of slugs, so the
        // adapter derives the slug from the URL.
        const inlineRelatedPosts = mapRelatedUseCases(blogData?.related_use_cases);

        // ── Category use cases — the bottom "Our Related Insights" grid ────────
        // Optional supporting content: a failure degrades to null, never a 404/500.
        // Every lookup below is independent, so each is started now and all are awaited together.
        const categoryPostsRequest: Promise<any[] | null> = id
            ? (getRequest(`${APIURL.USE_CASE}?use_cases_categories=${id}&per_page=6&_fields=${LISTING_FIELDS}`).catch(
                  () => null
              ) as Promise<any[] | null>)
            : Promise.resolve(null);

        // ── Thumbnails for inline "Read more / Read also" links ────────────────
        // These links point at other use cases OR at blog posts, so the slugs are
        // looked up against both endpoints and merged into one image pool.
        const rawContent: string = blogData?.content?.rendered ?? '';
        const readLinkSlugs: string[] = [];
        const pRegex = /<p\b[^>]*>([\s\S]*?)<\/p>/gi;
        let pMatch: RegExpExecArray | null;
        while ((pMatch = pRegex.exec(rawContent)) !== null) {
            const inner = pMatch[1];
            const lead = inner
                .replace(/<[^>]+>/g, '')
                .replace(/&#8211;|&nbsp;/g, ' ')
                .trim()
                .toLowerCase();
            if (!/^read (more|also)\b/.test(lead)) continue;
            const a = inner.match(/<a\b[^>]+href="([^"]+)"/i);
            if (!a) continue;
            const slug = slugFromLink(a[1]);
            if (slug) readLinkSlugs.push(slug);
        }

        const uniqueSlugs = [...new Set(readLinkSlugs)];
        const slugQuery = uniqueSlugs.map((s) => `slug[]=${encodeURIComponent(s)}`).join('&');
        // Either lookup failing only costs a thumbnail.
        const readLinkPostsRequest: Promise<any[]> = uniqueSlugs.length
            ? Promise.all([
                  getRequest(`${APIURL.USE_CASE}?${slugQuery}&per_page=20&_fields=${LISTING_FIELDS}`).catch(() => []),
                  getRequest(
                      `${APIURL.BLOGS_LISTING}?${slugQuery}&per_page=20&_fields=id,title,slug,blog_image,featured_image_url,unique_categories`
                  ).catch(() => [])
              ]).then(([useCaseMatches, blogMatches]) => [...((useCaseMatches as any[]) ?? []), ...((blogMatches as any[]) ?? [])])
            : Promise.resolve([]);

        // ── Bottom "Our Related Insights" grid ────────────────────────────────
        // `related_use_cases` is the editorially-chosen list, but it carries no
        // image, so the same posts are re-fetched by id to get their card fields.
        const includeIds = inlineRelatedPosts.map((p: any) => p.id).join(',');
        const relatedCardsRequest: Promise<any[]> = inlineRelatedPosts.length
            ? (getRequest(`${APIURL.USE_CASE}?include=${includeIds}&per_page=6&_fields=${LISTING_FIELDS}`)
                  .then((cards) => (cards as any[]) ?? [])
                  .catch(() => []) as Promise<any[]>)
            : Promise.resolve([]);

        // Breadcrumb category. A failed lookup only drops the breadcrumb — never blanks the article itself.
        const categoryRequest: Promise<any> = id
            ? getRequest<any>(`${APIURL.USE_CASE_CATEGORIES}/${id}`)
                  .then(withCategoryDisplayName)
                  .catch((categoryError) => {
                      reportError(categoryError, {context: 'getServerSideProps:useCaseDetail:category'});
                      return null;
                  })
            : Promise.resolve(null);

        // Every term, to pick the author from all of the use case's categories (as the listing does).
        const allCategoriesRequest = fetchUseCaseCategories('getServerSideProps:useCaseDetail:allCategories');

        const [categoryPosts, readLinkPosts, relatedCards, category, allCategories] = await Promise.all([
            categoryPostsRequest,
            readLinkPostsRequest,
            relatedCardsRequest,
            categoryRequest,
            allCategoriesRequest
        ]);
        const postCategorySlugs = allCategories.filter((c) => blogData.use_cases_categories?.includes(c.id)).map((c) => c.slug);
        // The breadcrumb term still counts if the full list failed to load.
        const authorInfo = resolveUseCaseAuthor([...postCategorySlugs, category?.slug]) ?? null;
        const categoryResponse = categoryPosts;

        // Only exclude the editorial picks from the grid when the content actually
        // renders them inline at a [related_post] shortcode — otherwise excluding
        // them would leave the grid empty (this post type has no such shortcodes).
        const rendersRelatedInline = /\[related_post(?:_\d+)?\]/.test(rawContent) || rawContent.includes('debut-related-card');

        let latestInsights: any[] | null = null;
        const gridSource = relatedCards.length ? relatedCards : categoryPosts;
        if (Array.isArray(gridSource)) {
            const excludeIds = new Set(rendersRelatedInline ? inlineRelatedPosts.map((p: any) => p?.id) : []);
            latestInsights = gridSource.filter((p: any) => p.id !== blogData.id && !excludeIds.has(p.id)).slice(0, 3);
        }

        setEdgeCache(res);
        return {
            props: {
                blogData,
                query,
                id,
                categoryResponse: categoryResponse || null,
                latestInsights: latestInsights || null,
                inlineRelatedPosts,
                readLinkPosts,
                category,
                authorInfo
            }
        };
    } catch (error) {
        // Reaching here means an infrastructure/transport failure (WordPress down, timeout, parse error) —
        // NOT "page missing". Returning notFound here would de-index live pages during an outage.
        // Emit HTTP 500 so Google retries instead of dropping the URL.
        reportError(error, {context: 'getServerSideProps:useCaseDetail'});
        if (res) res.statusCode = 500;
        return {
            props: {
                blogData: null,
                query,
                id: null,
                categoryResponse: null,
                latestInsights: null,
                inlineRelatedPosts: [],
                readLinkPosts: [],
                category: null,
                authorInfo: null
            }
        };
    }
};
